import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';

import { AnimationShell, Button, Chip, Icons, Link, Typography } from '@/components';
import { Route } from '@/constants';
import { isBoolean } from '@/types';
import { cn } from '@/utils/styles';

import { useIsOpportunityEnabled, useOpportunityDetails, useSendForRevision } from '../../../hooks';

import { useBelongsToMyCompany } from '@/features/company/hooks';
import { ASSETS_BASE_URL } from '@/config';
import { useSendOpportunityStats } from '@/features/stats';
import { useNotifications } from '@/features/notifications';
import { getChipFromOpportunity } from '../../../helpers';
import { GenerateTeaser } from '@/features/files/components/generate-teaser';
import { FloatingActionButtons } from './floating-action-buttons';

export type OpportunityHeaderViewProps = {
  readonly id: string;
  readonly setIsOpen: (isOpen: boolean) => void;
};

export function OpportunityHeaderView({ id, setIsOpen }: OpportunityHeaderViewProps) {
  const { t } = useTranslation(['opportunities']);
  const { add } = useNotifications();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();
  const { normalizedOpportunity: opportunity } = useOpportunityDetails(id);
  const { pathname } = useLocation();

  const isInSellingOpportunityRoute = /^\/selling\/opportunities\/[^/]+\/?$/.test(pathname);
  const statusName = opportunity?.status?.name;
  const isEnable = useIsOpportunityEnabled(opportunity);
  const opportunityBelongsToMyCompany = useBelongsToMyCompany(opportunity?.company);
  const canOffer = opportunity?.isPublished && !opportunityBelongsToMyCompany;

  const teaserId = opportunity?.files.find(({ type }) => type === 'teaser')?.file.id;
  const canShowOpenButton =
    opportunityBelongsToMyCompany &&
    isInSellingOpportunityRoute &&
    (statusName === 'published' || statusName === 'closed');

  const handleOnClick = canOffer ? () => setIsOpen(true) : () => setIsModalOpen(true);
  const sendDownloadStats = useSendOpportunityStats({ type: 'download_teaser' });

  const sendStatsAndOpen = () => {
    if (!opportunity) return;
    sendDownloadStats(opportunity?.id);
    window.open(`${ASSETS_BASE_URL}/${teaserId}`, '_blank');
  };

  const handleOnClickTeaser = canOffer
    ? () => sendStatsAndOpen()
    : () => window.open(`${ASSETS_BASE_URL}/${teaserId}`, '_blank');

  if (isBoolean(isEnable) && !isEnable) {
    navigate(Route.Home);
  }

  const canSendForRevision =
    opportunity?.isDraft && opportunity?.projects?.filter((project) => project.details?.isDraft).length;

  const sendForRevision = useSendForRevision(opportunity);
  const saveForLater = useCallback(() => {
    add({
      id: new Date().toString(),
      title: 'Project saved successfully',
      description: 'Your project has been saved for later.',
    });
  }, [add]);

  const chip = opportunity ? getChipFromOpportunity(opportunity) : null;

  if (!opportunity) return null;

  return (
    <>
      <AnimationShell type="opacity" className={cn('mb-0')}>
        <div className="flex w-full flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          {/* MOBILE + TABLET: special layout */}
          <div className="flex flex-col gap-3 lg:hidden">
            <div className="flex items-center gap-2">
              {chip && (
                <Chip color={chip.color} className={'text-xxs md:text-[10px]'}>
                  {chip.name}
                </Chip>
              )}
            </div>
            <div className="flex items-center">
              <Icons size="md" icons={opportunity.technologies.icons.states} className="shrink-0" />
              <AnimationShell type="slide-left">
                <Typography as="h1" size="heading-2" color="primary" weight="semibold" className="ml-2 text-[22px]">
                  {opportunity.name}
                </Typography>
              </AnimationShell>
            </div>

          </div>

          {/* DESKTOP: Layout horizontal */}
          <div className="hidden lg:flex lg:flex-wrap lg:items-center lg:gap-3">
            <Icons size="lg" icons={opportunity.technologies.icons.states} className="shrink-0" />

            <AnimationShell type="slide-left">
              <Typography as="h1" size="heading-2" color="primary" weight="semibold">
                {opportunity.name}
              </Typography>
            </AnimationShell>

            <div className="flex items-center gap-2">
              {opportunityBelongsToMyCompany && canSendForRevision ? null : (
                <>
                  {chip && <Chip color={chip.color}>{chip.name}</Chip>}
                  {statusName === 'published' && <Chip color="success">{t('published')}</Chip>}
                </>
              )}
            </div>

            {canShowOpenButton && (
              <Link
                to={`${Route.Opportunities}/${id}`}
                target="_blank"
                className="hover:bg-grey-50 rounded-md bg-gray-200 p-2"
              >
                <Typography
                  as="span"
                  className="font-heading flex items-center justify-center text-base"
                  weight="semibold"
                >
                  Open Opportunity
                </Typography>
              </Link>
            )}
          </div>

          {/* Right side: Action buttons (desktop only) */}
          <div className="hidden items-center justify-end gap-3 lg:flex">
            {/* Management buttons for the owner */}
            {opportunityBelongsToMyCompany && canSendForRevision && (
              <AnimationShell type="slide-left" className="flex gap-2">
                <Button size="md" color="secondary" onClick={sendForRevision}>
                  {t('Send for revision')}
                </Button>
                <Button size="md" color="neutral" onClick={saveForLater}>
                  {t('Save for later')}
                </Button>
              </AnimationShell>
            )}

            {/* Main buttons */}
            <GenerateTeaser opportunity={opportunity} />

            {teaserId && (
              <Button size="md" color="neutral" icon="download" onClick={handleOnClickTeaser}>
                {t('Download Teaser')}
              </Button>
            )}

            {!opportunityBelongsToMyCompany && (
              <Button size="md" color="secondary" icon="my-offers" onClick={handleOnClick}>
                {t('Express Interest')}
              </Button>
            )}
          </div>
        </div>
      </AnimationShell>

      {/* Floating buttons on mobile/tablet */}
      <FloatingActionButtons
        opportunityId={id}
        teaserId={teaserId}
        opportunityBelongsToMyCompany={opportunityBelongsToMyCompany}
        canSendForRevision={!!canSendForRevision}
        canShowOpenButton={canShowOpenButton}
        handleOnClickTeaser={handleOnClickTeaser}
        handleOnClick={handleOnClick}
        sendForRevision={sendForRevision}
        saveForLater={saveForLater}
        t={t}
      />
    </>
  );
}
